// Playbook Library table — FE-044.
//
// Columns: Name (link), System/Custom badge, Applicable scopes,
// Last edited, Applications in last 30 days, Actions (Open editor /
// Apply / Duplicate / Archive | Restore / Delete).
//
// The Apply action opens the shared Apply Playbook modal. The final
// apply submission stays disabled there until BE-046 lands.

import { Link } from "react-router-dom";

import { Table, THead, TBody, Tr, Th, Td } from "@/design-system";

import type { PlaybookListRow } from "./types";


export interface PlaybookListTableProps {
  rows: PlaybookListRow[];
  busyId: string | null;
  onOpenEditor: (row: PlaybookListRow) => void;
  onApply: (row: PlaybookListRow) => void;
  onDuplicate: (row: PlaybookListRow) => void;
  onArchive: (row: PlaybookListRow) => void;
  onRestore: (row: PlaybookListRow) => void;
  onDelete: (row: PlaybookListRow) => void;
}


function formatRelativeDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const date = new Date(iso);
    return date.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "—";
  }
}


function ScopesBadge({ scopes }: { scopes: PlaybookListRow["applicable_scopes"] }) {
  if (!scopes || scopes.length === 0) {
    return <span className="text-text-tertiary">—</span>;
  }
  return (
    <span data-testid="playbook-scopes" className="inline-flex flex-wrap gap-1">
      {scopes.map((s) => (
        <span
          key={s}
          className="rounded-full bg-recessed px-2 py-0.5 text-[11px] capitalize text-text-secondary"
        >
          {s.replaceAll("_", " ")}
        </span>
      ))}
    </span>
  );
}


function SystemBadge({ isSystem }: { isSystem: boolean }) {
  return (
    <span
      data-testid={
        isSystem ? "playbook-badge-system" : "playbook-badge-custom"
      }
      className={[
        "inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium",
        isSystem
          ? "bg-teal-tint text-teal"
          : "bg-recessed text-text-secondary",
      ].join(" ")}
    >
      {isSystem ? "System" : "Custom"}
    </span>
  );
}


export function PlaybookListTable({
  rows, busyId,
  onOpenEditor, onApply, onDuplicate,
  onArchive, onRestore, onDelete,
}: PlaybookListTableProps) {
  return (
    <div data-testid="playbook-list-table" className="overflow-x-auto">
      <Table scroll={false}>
        <THead>
          <Tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-text-tertiary">
            <Th>Name</Th>
            <Th>Type</Th>
            <Th>Scopes</Th>
            <Th>Last edited</Th>
            <Th>Applied (30d)</Th>
            <Th className="text-right">Actions</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => {
            const archived = !row.is_active;
            const busy = busyId === row.playbook_id;
            return (
              <Tr
                key={row.playbook_id}
                data-testid={`playbook-row-${row.playbook_id}`}
                className="border-b border-border-subtle/60 last:border-b-0"
              >
                <Td className="py-3">
                  <Link
                    to={`/dashboard/playbooks/${row.playbook_id}/edit`}
                    data-testid={`playbook-name-link-${row.playbook_id}`}
                    className="font-medium text-text-primary hover:text-teal hover:underline underline-offset-2"
                  >
                    {row.name}
                  </Link>
                  {row.description && (
                    <div className="mt-0.5 text-xs text-text-tertiary">
                      {row.description}
                    </div>
                  )}
                </Td>
                <Td className="py-3">
                  <SystemBadge isSystem={row.is_system} />
                </Td>
                <Td className="py-3">
                  <ScopesBadge scopes={row.applicable_scopes} />
                </Td>
                <Td className="py-3 text-text-secondary">
                  {formatRelativeDate(row.updated_at)}
                </Td>
                <Td className="py-3 text-text-secondary">
                  {row.applications_30d}
                </Td>
                <Td className="py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      data-testid={`playbook-open-editor-${row.playbook_id}`}
                      onClick={() => onOpenEditor(row)}
                      className="rounded-md px-2 py-1 text-xs text-text-secondary hover:bg-recessed hover:text-text-primary"
                    >
                      Open
                    </button>
                    <button
                      type="button"
                      data-testid={`playbook-apply-${row.playbook_id}`}
                      onClick={() => onApply(row)}
                      className="rounded-md px-2 py-1 text-xs text-text-secondary hover:bg-recessed hover:text-text-primary"
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      data-testid={`playbook-duplicate-${row.playbook_id}`}
                      onClick={() => onDuplicate(row)}
                      disabled={busy}
                      className="rounded-md px-2 py-1 text-xs text-text-secondary hover:bg-recessed hover:text-text-primary disabled:opacity-50"
                    >
                      Duplicate
                    </button>
                    {archived ? (
                      <button
                        type="button"
                        data-testid={`playbook-restore-${row.playbook_id}`}
                        onClick={() => onRestore(row)}
                        disabled={busy}
                        className="rounded-md px-2 py-1 text-xs text-text-secondary hover:bg-recessed hover:text-text-primary disabled:opacity-50"
                      >
                        Restore
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid={`playbook-archive-${row.playbook_id}`}
                        onClick={() => onArchive(row)}
                        disabled={row.is_system || busy}
                        aria-disabled={row.is_system}
                        title={
                          row.is_system
                            ? "System Playbooks can't be archived."
                            : undefined
                        }
                        className="rounded-md px-2 py-1 text-xs text-text-secondary hover:bg-recessed hover:text-text-primary disabled:opacity-50"
                      >
                        Archive
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`playbook-delete-${row.playbook_id}`}
                      onClick={() => onDelete(row)}
                      disabled={row.is_system || busy}
                      aria-disabled={row.is_system}
                      title={
                        row.is_system
                          ? "System Playbooks can't be deleted."
                          : undefined
                      }
                      className="rounded-md px-2 py-1 text-xs text-ember hover:bg-ember-tint disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}
